import React from "react";
import classnames from "classnames";
import Select from "react-select";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Form,
  Input,
  Row,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import ImageUploadContainer from "../User/adding-user/ImageUploadContainer";

/** A client option of the Add Product select; `""` is "None". */
export type ClientOption = { value: string; label: string };

/** Which fields render as valid; a server validation error flips one to false. */
export type AddGlobalProductValidation = {
  title: boolean;
  alt_pic: boolean;
  productImg: boolean;
  country_code: boolean;
  client: boolean;
};

/** What ImageUploadContainer hands back: a picked file, or a media-library pick. */
export type PickedImage = {
  data?: string;
  imgName?: string;
  secure_url?: string;
  context?: unknown;
};

type AddGlobalProductViewProps = {
  canUseForm: boolean;
  title: string;
  sponsored: boolean;
  rating: number | string;
  selectedClient: ClientOption;
  clientsData: ClientOption[];
  loading: boolean;
  validation: AddGlobalProductValidation;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRatingChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onToggleSponsored: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectClient: (option: ClientOption | null) => void;
  onReceiveImage: (img: PickedImage | null) => void;
  onAdd: (e: React.MouseEvent) => void;
};

const AddGlobalProductView = ({
  // perms
  canUseForm,

  // values
  title,
  sponsored,
  rating,
  selectedClient,
  clientsData,
  loading,
  validation,

  // handlers
  onTitleChange,
  onRatingChange,
  onToggleSponsored,
  onSelectClient,
  onReceiveImage,
  onAdd,
}: AddGlobalProductViewProps) => {
  return (
    <>
      {/* Notifications */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Add Product" parentName="Products" />

      <Container className="mt--6" fluid>
        {canUseForm ? (
          <Card>
            <CardHeader>
              <Row className="align-items-center">
                <Col xs="8">
                  <h5 className="h3 mb-0">Add Product</h5>
                </Col>
              </Row>
            </CardHeader>

            <CardBody>
              <Form role="form">
                <Row className="text-center">
                  <Col>
                    <ImageUploadContainer ImageUpload={onReceiveImage} />
                    {!validation.productImg ? (
                      <InputGroup className="invalid-feedback">
                        Required State.
                      </InputGroup>
                    ) : null}
                  </Col>
                </Row>

                {/* Title */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Title
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-box-2" />
                      </InputGroupText>
                      <Input
                        placeholder="Insert Product Title"
                        name="title"
                        type="text"
                        invalid={!validation.title}
                        value={title}
                        onChange={onTitleChange}
                      />
                    </InputGroup>
                    {!validation.title ? (
                      <InputGroup className="invalid-feedback">
                        Required State.
                      </InputGroup>
                    ) : null}
                  </Col>
                </FormGroup>

                {/* Client */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Client
                  </Label>
                  <Col md="7">
                    <Select
                      name="clients"
                      options={clientsData}
                      onChange={onSelectClient}
                      isClearable
                      isSearchable
                      value={selectedClient}
                    />
                    {!validation.client ? (
                      <InputGroup className="invalid-feedback">
                        Required State.
                      </InputGroup>
                    ) : null}
                  </Col>
                </FormGroup>

                {/* SCRUM-312: the Active toggle was removed here. The flag was
                    never read on the public side — no sitemap, listing or render
                    query filtered on it — so switching a product off changed
                    nothing a visitor could see, which is the editor confusion
                    the ticket describes. New products take the schema default
                    (`active: true`); the field itself is left in place. */}

                {/* Sponsored */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Sponsored
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <label className="custom-toggle custom-toggle-danger mr-1">
                        <input
                          checked={sponsored}
                          onChange={onToggleSponsored}
                          name="sponsored"
                          type="checkbox"
                        />
                        <span
                          className="custom-toggle-slider rounded-circle"
                          data-label-off="No"
                          data-label-on="Yes"
                        />
                      </label>
                    </InputGroup>
                  </Col>
                </FormGroup>

                {/* Rating */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Rating
                  </Label>
                  <Col md="7">
                    <InputGroup className={classnames("input-group-merge")}>
                      <InputGroupText>
                        <i className="ni ni-box-2" />
                      </InputGroupText>
                      <Input
                        placeholder="Insert Product Rating"
                        name="rating"
                        type="number"
                        step="0.1"
                        min="0"
                        max="10"
                        value={rating}
                        onChange={onRatingChange}
                      />
                    </InputGroup>
                  </Col>
                </FormGroup>

                <FormGroup className="row">
                  <Col md="10">
                    <Col className="text-right">
                      <Button
                        className="my-4"
                        color="primary"
                        type="submit"
                        onClick={onAdd}
                        disabled={loading}
                      >
                        {loading ? "Uploading…" : "Add"}
                      </Button>
                    </Col>
                  </Col>
                </FormGroup>
              </Form>
            </CardBody>
          </Card>
        ) : null}
      </Container>
    </>
  );
};

export default AddGlobalProductView;
